import React from 'react';
import { Box } from '@withjoy/joykit';
import { Check } from '@withjoy/joykit/icons';
import { animationTransition } from '@shared/utils/animationTransition';
import { useDesignGalleryFiltersContext } from '../../../DesignGalleryFiltersProvider';
import type { FilterFacet } from '../Filter.types';
import { useToggleFilter } from '../useToggleFilter';

type FilterTogglePillProps = Readonly<{
  /** A one-option facet (see `isToggleFacet`); the pill toggles that option. */
  facet: FilterFacet;
  pillHeight?: number;
}>;

/**
 * A top-level on/off pill for a single-option facet — "Free" (Arun,
 * 2026-10-14). Reads like the other pills (same height and radius), fills dark with a check
 * when on, and flips the option directly instead of opening anything.
 */
export const FilterTogglePill = ({ facet, pillHeight = 40 }: FilterTogglePillProps) => {
  const { activePrintFilters } = useDesignGalleryFiltersContext();
  const toggleFilter = useToggleFilter();
  const option = facet.options[0];
  if (!option) return null;
  const isOn = activePrintFilters.has(option.compositeCode);

  return (
    <Box
      as="button"
      type="button"
      aria-pressed={isOn}
      onClick={() => toggleFilter(option.compositeCode, !isOn, { source: 'togglePill', facet: facet.groupCode })}
      height={pillHeight}
      paddingX={5}
      borderRadius={24}
      display="inline-flex"
      alignItems="center"
      columnGap={2}
      typographyVariant="button1"
      whiteSpace="nowrap"
      backgroundColor={isOn ? 'mono14' : 'mono2'}
      color={isOn ? 'white' : 'mono14'}
      transition={animationTransition('background-color')}
      _hover={{ backgroundColor: isOn ? 'mono13' : 'mono3' }}
      data-testid={`filter-toggle-pill-${facet.groupCode}`}
    >
      {isOn && (
        <Check
          size={16}
          aria-hidden="true"
        />
      )}
      {facet.label}
    </Box>
  );
};
